import { Filters, useMakeCommonFilterDef, type FilterDefConfig } from '@tractian/ui-filters'
import { CalendarStartIcon, LocationIcon, SendIcon, SensorIcon, UserIcon } from '@tractian/icons'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

import type { User } from '@/features/assessments/api/users-list'
import { useFeature } from '@/features/feature-flags/hooks/use-feature'
import {
  ASSESSMENT_STATUS_CONFIG,
  ASSESSMENT_STATUS_ORDER
} from '@/features/assessments/domain/assessment-status'
import { COUNTRY_OPTIONS, flagEmoji } from '@/features/assessments/utils/countries'
import { FiltersProvider } from '@/components/toolkit-filters/filters-provider'
import { makeAssessmentDateRangeFilterDef } from '@/features/assessments/utils/assessment-date-range-filter-def'
import type { DateRangeFilter } from '@/features/assessments/utils/assessment-date-range-params'

type AssessmentFilterModel = {
  status: string | null
  country: string[]
  dealOwnerUserID: string[]
  salesEngineerUserID: string[]
  scopedDevices: string[]
  hasStarted: string | null
  isSubmitted: string | null
  startedAt: DateRangeFilter
  lastUpdatedAt: DateRangeFilter
  finishedAt: DateRangeFilter
}

export type { AssessmentFilterModel }

export const defaultAssessmentFilterValues: AssessmentFilterModel = {
  status: null,
  country: [],
  dealOwnerUserID: [],
  salesEngineerUserID: [],
  scopedDevices: [],
  hasStarted: null,
  isSubmitted: null,
  startedAt: {},
  lastUpdatedAt: {},
  finishedAt: {}
}

// Device families accepted by the API `scopedDevices` filter.
export const SCOPED_DEVICES = [
  'smartTrac',
  'energyTrac',
  'omniTrac',
  'bandTrac',
  'uniTrac'
] as const

type AssessmentFiltersProviderProps = {
  users: User[] | undefined
  onFiltersChange?: (values: AssessmentFilterModel) => void
  children: ReactNode
}

export function AssessmentFiltersProvider({
  users,
  onFiltersChange,
  children
}: AssessmentFiltersProviderProps) {
  const { t } = useTranslation('plantAssessment')
  // With multiPhase the assessment status is gone: no status filter, and a
  // `status` left in old links or saved preferences is dropped.
  const multiPhase = useFeature('multiPhase')

  const userOptions = (users ?? []).map(u => ({ label: u.name, value: u.id }))

  const countryOptions = COUNTRY_OPTIONS.map(c => ({
    label: `${flagEmoji(c.value)} ${c.label}`,
    value: c.value
  }))

  const statusOptions = ASSESSMENT_STATUS_ORDER.map(value => {
    const { Icon, colorHex } = ASSESSMENT_STATUS_CONFIG[value]
    return {
      value,
      label: t(`assessmentTable.status.${value}`),
      color: colorHex,
      icon: <Icon />
    }
  })

  const statusFilterDef = useMakeCommonFilterDef({
    key: 'status',
    icon: <LocationIcon />,
    label: 'Status',
    options: statusOptions,
    defaultPinned: true,
    mode: 'single'
  })

  const countryFilterDef = useMakeCommonFilterDef({
    key: 'country',
    icon: <LocationIcon />,
    label: 'Country',
    options: countryOptions,
    defaultPinned: true,
    mode: 'multiple'
  })

  const dealOwnerFilterDef = useMakeCommonFilterDef({
    key: 'dealOwnerUserID',
    icon: <UserIcon />,
    label: 'Deal Owner',
    options: userOptions,
    isLoading: !users,
    defaultPinned: true,
    mode: 'multiple'
  })

  const seFilterDef = useMakeCommonFilterDef({
    key: 'salesEngineerUserID',
    icon: <UserIcon />,
    label: 'Sales Engineer',
    options: userOptions,
    isLoading: !users,
    defaultPinned: true,
    mode: 'multiple'
  })

  const scopedDevicesFilterDef = useMakeCommonFilterDef({
    key: 'scopedDevices',
    icon: <SensorIcon />,
    label: t('assessmentFilters.scopedDevices.label'),
    options: SCOPED_DEVICES.map(value => ({
      value,
      label: t(`assessmentFilters.scopedDevices.options.${value}`)
    })),
    defaultPinned: true,
    mode: 'multiple'
  })

  const yesNoOptions = [
    { value: 'true', label: t('assessmentFilters.yes') },
    { value: 'false', label: t('assessmentFilters.no') }
  ]

  const hasStartedFilterDef = useMakeCommonFilterDef({
    key: 'hasStarted',
    icon: <CalendarStartIcon />,
    label: t('assessmentFilters.hasStarted'),
    options: yesNoOptions,
    defaultPinned: true,
    mode: 'single'
  })

  const isSubmittedFilterDef = useMakeCommonFilterDef({
    key: 'isSubmitted',
    icon: <SendIcon />,
    label: t('assessmentFilters.isSubmitted'),
    options: yesNoOptions,
    defaultPinned: true,
    mode: 'single'
  })

  const startedAtFilterDef = makeAssessmentDateRangeFilterDef({
    key: 'startedAt',
    label: 'Scope started date'
  })

  const lastUpdatedAtFilterDef = makeAssessmentDateRangeFilterDef({
    key: 'lastUpdatedAt',
    label: 'Last updated'
  })

  const finishedAtFilterDef = makeAssessmentDateRangeFilterDef({
    key: 'finishedAt',
    label: 'Finished'
  })

  const filterDefs = [
    ...(multiPhase ? [] : [statusFilterDef]),
    countryFilterDef,
    dealOwnerFilterDef,
    seFilterDef,
    scopedDevicesFilterDef,
    hasStartedFilterDef,
    isSubmittedFilterDef,
    startedAtFilterDef,
    lastUpdatedAtFilterDef,
    finishedAtFilterDef
  ]

  return (
    <FiltersProvider
      name="assessments"
      filterDefs={filterDefs as FilterDefConfig<AssessmentFilterModel>[]}
      defaultValues={defaultAssessmentFilterValues}
      useUrlValues
      ignoredKeys={multiPhase ? ['status'] : undefined}
      onFiltersChange={onFiltersChange}
    >
      {children}
    </FiltersProvider>
  )
}

export function AssessmentFiltersToolbar() {
  return (
    <Filters.Toolbar>
      <Filters.Toolbar.Content
        allowAdd
        allowSave={false}
        allowClear
        classNames={{ trigger: 'bg-white' }}
      />
    </Filters.Toolbar>
  )
}
